import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LogoElement, MobiferLogo, VerticalStepper, colors } from 'remote/global';
import { useTranslation } from 'react-i18next';
import AccountRegistrationForm from 'components/forms/AccountFactory/AccountRegistrationForm';
import { FormStep } from 'utils/data/data-types';
import { Link, useParams } from 'react-router-dom';
import { ROUTES } from 'utils/data/routes';
import { useOperatorsOperatorToken } from 'api/queries/operators';
import {
  CompanyContactDetailsSchemaType,
  CompanyDetailsSchemaType,
} from 'components/forms/AccountFactory/types';
import { OperatorDtoSchemaType } from 'components/forms/types';

interface Props {
  steps: FormStep[];
  activeStep: number;
}
function SignUpSidebar({ steps, activeStep }: Props) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        height: '100%',
        background: colors.uiBlue50,
        position: 'relative',
        zIndex: 2,
        maxWidth: [300, 400, 400, 500],
      }}
    >
      <Box
        sx={{
          height: '100%',
          background: colors.uiBlue50,
          p: '48px 48px 0 48px',
        }}
      >
        <Link to={ROUTES.BaseRoutes.ROOT}>
          <MobiferLogo />
        </Link>
        <Typography
          sx={{
            mt: '90px',
            mb: '34px',
            fontSize: '24px',
          }}
          variant='h2'
        >
          {t('actions.user.createAccount')}
        </Typography>
        <VerticalStepper steps={steps} activeStep={activeStep} />
      </Box>
    </Box>
  );
}

function getOperatorCompanyDetails(operator: OperatorDtoSchemaType): CompanyDetailsSchemaType {
  return (({ address, country, legalName, regCode, operatingLicense, companyVat, type }) => ({
    address: address!,
    country,
    legalName,
    regCode,
    operatingLicense,
    companyVat,
    type,
  }))(operator);
}

function getOperatorCompanyContactDetails(
  operator: OperatorDtoSchemaType
): CompanyContactDetailsSchemaType {
  return (({
    brandName,
    contactEmail = '',
    contactPhoneNumber = '',
    emergencyPhoneNumber = '',
    emergencyEmail,
    website,
    offerContactEmail = '',
    offerContactPhone = '',
  }) => ({
    brandName,
    contactEmail,
    contactPhoneNumber,
    emergencyPhoneNumber,
    emergencyEmail,
    website,
    offerContactEmail,
    offerContactPhone,
  }))(operator);
}

function SignUpView() {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const largeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { t, i18n } = useTranslation();
  const { accessToken } = useParams();
  const { data: operator, isSuccess: isTokenSuccess } = useOperatorsOperatorToken(accessToken);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lang = urlParams.get('lang');

    if (!lang) return;
    i18n.changeLanguage(lang);
  }, [i18n]);

  const steps: FormStep[] = [
    {
      label: t('SignUpView.steps.account.title'),
      description: t('SignUpView.steps.account.subtitle'),
    },
    {
      label: t('SignUpView.steps.companyDetails.title'),
      description: t('SignUpView.steps.companyDetails.subtitle'),
    },
    {
      label: t('SignUpView.steps.companyContactDetails.title'),
      description: t('SignUpView.steps.companyContactDetails.subtitle'),
    },
    {
      label: t('SignUpView.steps.password.title'),
      description: t('SignUpView.steps.password.subtitle'),
    },
  ];

  if (accessToken != null && accessToken.length > 0 && !isTokenSuccess) return null;

  return (
    <Box sx={{ height: '100vh', display: 'flex', width: '100%', overflow: 'auto' }}>
      {largeScreen && <SignUpSidebar steps={steps} activeStep={activeStep} />}
      <AccountRegistrationForm
        steps={steps}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        {...(isTokenSuccess && {
          defaultValues: [
            {
              email: operator.offerContactEmail!,
              phone: operator.offerContactPhone!,
              firstName: '',
              lastName: '',
            },
            getOperatorCompanyDetails(operator),
            getOperatorCompanyContactDetails(operator),
          ],
        })}
      />
      <LogoElement
        color='blue'
        sx={{
          bottom: '0',
          right: '-90px',
          position: 'fixed',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

export default SignUpView;
